import {useState} from 'react';
import {SumUnitsMonth, SumUnitsSchool, SumUnitsRow} from '@/store/customerFile.ts';
import {ChevronDownIcon, ChevronUpIcon, ArrowDownTrayIcon} from '@heroicons/react/24/outline';

interface Props {
    unitsMonths: SumUnitsMonth[];
}

function fmtNum(n: number) {
    return n.toLocaleString('he-IL', {minimumFractionDigits: 1, maximumFractionDigits: 1});
}

/* DetailDrawer — floating overlay so it does NOT expand column width */
function DetailDrawer({rows, label}: {rows: SumUnitsRow[]; label: string}) {
    const [open, setOpen] = useState(false);
    if (rows.length === 0) return <span className="text-sm font-medium">{label}</span>;
    return (
        <div className="relative inline-block">
            <div className="flex items-center gap-1">
                <span className="text-sm font-medium">{label}</span>
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="text-xs text-blue-600 hover:underline flex items-center gap-0.5"
                >
                    פירוט
                    {open ? <ChevronUpIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />}
                </button>
            </div>
            {open && (
                <>
                    <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 bottom-full mb-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg p-2 text-xs space-y-0.5 min-w-[200px]">
                        {rows.map((r, i) => (
                            <div key={i} className="flex justify-between gap-3 text-gray-600">
                                <span className="truncate flex-1">{r.type_row_display}</span>
                                <span className="font-medium w-16 text-left shrink-0">{fmtNum(r.amount)}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

const USAGE_TYPES = new Set([11, 12, 13, 14, 15, 16, 17]);
const FREE_TYPES = new Set([23, 24]);
const PURCHASE_TYPE = 22;

function SchoolUnitsRow({school}: {school: SumUnitsSchool}) {
    const purchaseRows = school.rows.filter((r) => r.type_row === PURCHASE_TYPE);
    const usageRows = school.rows.filter((r) => USAGE_TYPES.has(r.type_row));
    const freeRows = school.rows.filter((r) => FREE_TYPES.has(r.type_row));

    return (
        <tr className="border-b border-gray-50">
            {/* Fixed column widths so detail drawer doesn't push layout */}
            <td className="py-2 px-3 text-sm font-medium text-gray-800 w-40 max-w-[160px] truncate">{school.school_name}</td>
            <td className="py-2 px-3 w-36">
                {school.purchased === 0
                    ? <span className="text-xs text-gray-400">—</span>
                    : <DetailDrawer rows={purchaseRows} label={fmtNum(school.purchased)} />
                }
            </td>
            <td className="py-2 px-3 w-36">
                <DetailDrawer rows={usageRows} label={fmtNum(school.used)} />
            </td>
            {school.free_load > 0 && (
                <td className="py-2 px-3 w-36">
                    <DetailDrawer rows={freeRows} label={fmtNum(school.free_load)} />
                </td>
            )}
        </tr>
    );
}

function MonthCard({monthData}: {monthData: SumUnitsMonth}) {
    const [open, setOpen] = useState(false);
    const hasFreeLoad = monthData.free_load > 0;
    return (
        <div className="border border-gray-200 rounded-xl mb-2 overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-right bg-white hover:bg-gray-50 transition-colors"
            >
                <div className="flex items-center gap-4 flex-wrap">
                    <strong className="text-sm font-semibold text-gray-900">{monthData.period}</strong>
                    <span className="text-sm text-gray-600">נרכשו: {fmtNum(monthData.purchased)}</span>
                    <span className="text-sm text-gray-600">נוצלו: {fmtNum(monthData.used)}</span>
                    {hasFreeLoad && (
                        <span className="text-sm text-gray-600">טעינות ללא תשלום: {fmtNum(monthData.free_load)}</span>
                    )}
                </div>
                <span className="shrink-0 text-gray-400">
                    {open ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                </span>
            </button>

            {open && (
                <div className="border-t border-gray-100 overflow-x-auto">
                    <table className="w-full text-right text-sm">
                        <thead>
                        <tr className="border-b border-gray-100 bg-gray-50/50">
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 w-40">מערכת</th>
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 w-36">יחידות שנרכשו</th>
                            <th className="py-2 px-3 text-xs font-medium text-gray-500 w-36">יחידות שנוצלו</th>
                            {hasFreeLoad && (
                                <th className="py-2 px-3 text-xs font-medium text-gray-500 w-36">טעינות ללא תשלום</th>
                            )}
                        </tr>
                        </thead>
                        <tbody>
                        {monthData.schools.map((school) => (
                            <SchoolUnitsRow key={school.school_id} school={school} />
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/* ── Export helpers ──────────────────────────────────── */

function buildSummaryCsv(months: SumUnitsMonth[]): string {
    const rows: string[][] = [['חודש', 'יחידות שנרכשו', 'יחידות שנוצלו', 'טעינות ללא תשלום']];
    for (const m of months) {
        rows.push([m.period, String(m.purchased), String(m.used), String(m.free_load)]);
    }
    return rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
}

function buildDetailedCsv(months: SumUnitsMonth[]): string {
    const rows: string[][] = [['חודש', 'מערכת', 'סוג פעולה', 'כמות']];
    for (const m of months) {
        for (const s of m.schools) {
            for (const r of s.rows) {
                rows.push([m.period, s.school_name, r.type_row_display, String(r.amount)]);
            }
        }
    }
    return rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
}

function downloadCsv(content: string, filename: string) {
    const bom = '\uFEFF';
    const blob = new Blob([bom + content], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export function CustomerFileUnits({unitsMonths}: Props) {
    const [exportOpen, setExportOpen] = useState(false);

    return (
        <div>
            {/* Toolbar */}
            <div className="flex items-center justify-end mb-3">
                <div className="relative inline-block">
                    <button
                        type="button"
                        onClick={() => setExportOpen((v) => !v)}
                        disabled={unitsMonths.length === 0}
                        className="inline-flex items-center gap-1.5 text-xs border border-gray-300 hover:border-gray-400 text-gray-700 bg-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                        ייצוא לאקסל
                        <ChevronDownIcon className="w-3 h-3" />
                    </button>
                    {exportOpen && (
                        <>
                            <div className="fixed inset-0 z-10" onClick={() => setExportOpen(false)} />
                            <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden min-w-[140px]">
                                <button
                                    type="button"
                                    className="w-full text-right text-sm px-4 py-2.5 hover:bg-gray-50 transition-colors"
                                    onClick={() => { downloadCsv(buildSummaryCsv(unitsMonths), 'יחידות-מרוכז.csv'); setExportOpen(false); }}
                                >
                                    מרוכז
                                </button>
                                <button
                                    type="button"
                                    className="w-full text-right text-sm px-4 py-2.5 hover:bg-gray-50 transition-colors"
                                    onClick={() => { downloadCsv(buildDetailedCsv(unitsMonths), 'יחידות-מפורט.csv'); setExportOpen(false); }}
                                >
                                    מפורט
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {unitsMonths.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">אין נתוני יחידות</p>
            ) : (
                <>
                    <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700">
                        הטבלה מציגה סיכום בלבד. ניתן לפתוח פירוט לכל חודש, מערכת או קטגוריה בלחיצה.
                    </div>
                    {unitsMonths.map((m) => (
                        <MonthCard key={m.period} monthData={m} />
                    ))}
                </>
            )}
        </div>
    );
}

